(() => {
  'use strict';

  const VERSION = '3.2.2';

  if (window.IVRCartDrawer?.version === VERSION) {
    return;
  }

  const state = {
    giftLock: false,
    timer: null,
    observer: null,
    observedDrawer: null,
    subscribed: false,
  };

  /* =========================================================
     ELEMENTOS / ROTAS
  ========================================================= */

  const drawer = () =>
    document.querySelector('cart-drawer[data-ivr-cart-drawer="true"]') ||
    document.querySelector('cart-drawer');

  const root = () => {
    return window.Shopify?.routes?.root || '/';
  };

  const route = (name, fallback) => {
    return window.routes?.[name] || fallback;
  };

  const sectionIds = () => {
    return ['cart-drawer', 'cart-icon-bubble'];
  };

  const parse = (html = '') => {
    return new DOMParser().parseFromString(html, 'text/html');
  };

  /* =========================================================
     DINHEIRO
  ========================================================= */

  function money(cents) {
    const value = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'BRL';

    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
      }).format(value);
    } catch (error) {
      return `R$ ${value.toFixed(2).replace('.', ',')}`;
    }
  }

  /* =========================================================
     ATUALIZAÇÃO DO ÍCONE DO CARRINHO
  ========================================================= */

  function replaceBubble(html) {
    if (!html) return;

    const documentHTML = parse(html);

    const source = documentHTML.getElementById('cart-icon-bubble');
    const target = document.getElementById('cart-icon-bubble');

    if (!source || !target) return;

    target.innerHTML = source.innerHTML;
  }

  /* =========================================================
     ATUALIZAÇÃO DO DRAWER
  ========================================================= */

  function replaceDrawer(html) {
    if (!html) return;

    const targetDrawer = drawer();

    if (!targetDrawer) return;

    const documentHTML = parse(html);

    const sourceDrawer = documentHTML.querySelector('cart-drawer');
    const sourceCart = documentHTML.getElementById('CartDrawer');

    const targetCart = targetDrawer.querySelector('#CartDrawer');

    if (sourceDrawer) {
      targetDrawer.classList.toggle(
        'is-empty',
        sourceDrawer.classList.contains('is-empty')
      );
    }

    if (sourceCart && targetCart) {
      targetCart.innerHTML = sourceCart.innerHTML;
    }
  }

  /* =========================================================
     ABRIR DRAWER
  ========================================================= */

  function openDrawer(activeElement) {
    const open = () => {
      const element = drawer();

      if (!element) return;

      if (
        activeElement &&
        typeof element.setActiveElement === 'function'
      ) {
        element.setActiveElement(activeElement);
      }

      if (typeof element.open === 'function') {
        element.open(activeElement);
      }
    };

    const currentDrawer = drawer();

    if (typeof currentDrawer?.open === 'function') {
      open();
      return;
    }

    /*
      Caso o JS customizado carregue antes do cart-drawer.js
      nativo do Dawn, esperamos o componente ser registrado.
    */

    if (window.customElements?.whenDefined) {
      window.customElements
        .whenDefined('cart-drawer')
        .then(() => {
          setTimeout(open, 0);
        });

      return;
    }

    setTimeout(open, 100);
  }

  /* =========================================================
     PUBSUB DAWN
  ========================================================= */

  function publishCartUpdate(data) {
    if (
      typeof window.publish !== 'function' ||
      !window.PUB_SUB_EVENTS?.cartUpdate
    ) {
      return;
    }

    try {
      window.publish(
        window.PUB_SUB_EVENTS.cartUpdate,
        {
          source: 'ivr-cart-drawer',
          cartData: data,
        }
      );
    } catch (error) {
      console.warn(
        '[IVR Cart Drawer] Não foi possível publicar cartUpdate.',
        error
      );
    }
  }

  /* =========================================================
     RENDERIZAR RESPOSTA AJAX
  ========================================================= */

  function render(data, activeElement) {
    if (!data?.sections) {
      openDrawer(activeElement);
      scheduleGifts();
      return;
    }

    const element = drawer();

    let renderedWithNativeDrawer = false;

    /*
      Primeiro tenta usar o renderContents original do Dawn.
    */

    if (
      element &&
      typeof element.renderContents === 'function'
    ) {
      try {
        if (
          activeElement &&
          typeof element.setActiveElement === 'function'
        ) {
          element.setActiveElement(activeElement);
        }

        element.renderContents(data);

        renderedWithNativeDrawer = true;
      } catch (error) {
        console.warn(
          '[IVR Cart Drawer] Render nativo falhou. Usando fallback.',
          error
        );
      }
    }

    /*
      Caso o Dawn ainda não esteja disponível,
      fazemos a atualização manual.
    */

    if (!renderedWithNativeDrawer) {
      replaceDrawer(
        data.sections['cart-drawer']
      );

      replaceBubble(
        data.sections['cart-icon-bubble']
      );

      openDrawer(activeElement);
    }

    const updatedDrawer = drawer();

    if (
      updatedDrawer &&
      typeof data.item_count === 'number'
    ) {
      updatedDrawer.classList.toggle(
        'is-empty',
        data.item_count === 0
      );
    }

    publishCartUpdate(data);
    syncCheckoutState();
    scheduleGifts();
  }

  /* =========================================================
     TRATAR JSON SHOPIFY
  ========================================================= */

  async function jsonResponse(response) {
    const text = await response.text();

    let data;

    try {
      data = JSON.parse(text);
    } catch (error) {
      throw new Error(
        'A Shopify retornou uma resposta inválida.'
      );
    }

    if (
      !response.ok ||
      data.status ||
      data.errors
    ) {
      let message =
        data.description ||
        data.message ||
        data.errors ||
        'Não foi possível atualizar o carrinho.';

      if (typeof message !== 'string') {
        try {
          message = JSON.stringify(message);
        } catch (error) {
          message =
            'Não foi possível atualizar o carrinho.';
        }
      }

      throw new Error(message);
    }

    return data;
  }

  /* =========================================================
     ADICIONAR PRODUTO
  ========================================================= */

  async function addItem(
    variantId,
    properties = {},
    activeElement
  ) {
    const form = new FormData();

    form.append(
      'id',
      String(variantId)
    );

    form.append(
      'quantity',
      '1'
    );

    Object.entries(properties).forEach(
      ([key, value]) => {
        form.append(
          `properties[${key}]`,
          value
        );
      }
    );

    /*
      Isso é fundamental para a Shopify devolver
      o carrinho lateral atualizado junto da requisição.
    */

    form.append(
      'sections',
      sectionIds().join(',')
    );

    form.append(
      'sections_url',
      window.location.pathname
    );

    const response = await fetch(
      route(
        'cart_add_url',
        '/cart/add.js'
      ),
      {
        method: 'POST',

        credentials: 'same-origin',

        headers: {
          Accept: 'application/json',
          'X-Requested-With':
            'XMLHttpRequest',
        },

        body: form,
      }
    );

    const data =
      await jsonResponse(response);

    render(
      data,
      activeElement
    );

    syncMegaFitDiscount(data);

    return data;
  }

  /* =========================================================
     ALTERAR / REMOVER PRODUTO
  ========================================================= */

  async function changeItem(
    lineKey,
    quantity,
    activeElement
  ) {
    const response = await fetch(
      route(
        'cart_change_url',
        '/cart/change.js'
      ),
      {
        method: 'POST',

        credentials: 'same-origin',

        headers: {
          Accept: 'application/json',

          'Content-Type':
            'application/json',

          'X-Requested-With':
            'XMLHttpRequest',
        },

        body: JSON.stringify({
          id: lineKey,

          quantity,

          sections:
            sectionIds(),

          sections_url:
            window.location.pathname,
        }),
      }
    );

    const data =
      await jsonResponse(response);

    render(
      data,
      activeElement
    );

    syncMegaFitDiscount(data);

    return data;
  }

  /* =========================================================
     PEGAR CARRINHO ATUAL
  ========================================================= */

  async function getCart() {
    let url =
      route(
        'cart_url',
        '/cart'
      );

    if (!/\.js(?:\?|$)/.test(url)) {
      url += '.js';
    }

    const response =
      await fetch(url, {
        credentials: 'same-origin',

        headers: {
          Accept: 'application/json',
        },
      });

    return jsonResponse(response);
  }


  /* =========================================================
     MEGAFIT — CONTROLE GLOBAL DO CÓDIGO
  ========================================================= */

  const MEGAFIT_CODE = 'MEGAFIT';
  let megaFitSyncLock = false;

  async function syncMegaFitDiscount(cart) {
    if (megaFitSyncLock || !cart) return cart;

    const items = Array.isArray(cart.items) ? cart.items : [];

    const hasMegaFitOrigin = items.some((item) => {
      return String(item?.properties?._combo_origin || '') === 'MEGA_FIT';
    });

    const discountCodes = Array.isArray(cart.discount_codes)
      ? cart.discount_codes
          .map((item) => String(item?.code || '').trim())
          .filter(Boolean)
      : [];

    const hasMegaFitCode = discountCodes.some(
      (code) => code.toUpperCase() === MEGAFIT_CODE
    );

    // Se não existe mais nenhum item originado pelo botão
    // Mega + Fit, o MEGAFIT precisa sair imediatamente.
    if (!hasMegaFitOrigin && hasMegaFitCode) {
      megaFitSyncLock = true;

      try {
        const remainingCodes = discountCodes.filter(
          (code) => code.toUpperCase() !== MEGAFIT_CODE
        );

        const response = await fetch(
          route('cart_update_url', '/cart/update.js'),
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              discount: remainingCodes.join(','),
              sections: sectionIds(),
              sections_url: window.location.pathname,
            }),
          }
        );

        const updatedCart = await jsonResponse(response);

        render(updatedCart);

        return updatedCart;
      } catch (error) {
        console.error(
          '[IVR Cart Drawer] Erro ao remover MEGAFIT:',
          error
        );
      } finally {
        megaFitSyncLock = false;
      }
    }

    return cart;
  }

  async function syncMegaFitFromCart() {
    try {
      const cart = await getCart();
      return await syncMegaFitDiscount(cart);
    } catch (error) {
      console.warn(
        '[IVR Cart Drawer] Não foi possível verificar o MEGAFIT:',
        error
      );
      return null;
    }
  }

  /* =========================================================
     RECARREGAR DRAWER
  ========================================================= */

  async function refresh(
    openAfter = true
  ) {
    const url =
      `${root()}?sections=${encodeURIComponent(
        sectionIds().join(',')
      )}`;

    const response =
      await fetch(url, {
        credentials: 'same-origin',

        headers: {
          Accept: 'application/json',

          'X-Requested-With':
            'XMLHttpRequest',
        },
      });

    const sections =
      await jsonResponse(response);

    replaceDrawer(
      sections['cart-drawer']
    );

    replaceBubble(
      sections['cart-icon-bubble']
    );

    if (openAfter) {
      openDrawer();
    }

    scheduleGifts();
    syncMegaFitFromCart();

    return sections;
  }

  /* =========================================================
     ESTADO DE LOADING DOS BOTÕES
  ========================================================= */

  function setLoading(
    button,
    loading,
    text = 'Carregando...'
  ) {
    if (!button) return;

    if (loading) {
      if (
        !button.dataset.ivrOriginalText
      ) {
        button.dataset.ivrOriginalText =
          button.textContent;
      }

      button.disabled = true;

      button.setAttribute(
        'aria-busy',
        'true'
      );

      button.textContent =
        text;

      return;
    }

    button.disabled = false;

    button.removeAttribute(
      'aria-busy'
    );

    if (
      button.dataset.ivrOriginalText
    ) {
      button.textContent =
        button.dataset.ivrOriginalText;

      delete button.dataset
        .ivrOriginalText;
    }
  }

  function buttonError(
    button,
    error
  ) {
    setLoading(
      button,
      false
    );

    if (!button) return;

    button.title =
      error?.message ||
      'Ocorreu um erro.';
  }

  /* =========================================================
     ORDER BUMP — VARIAÇÃO
  ========================================================= */

  function changeBumpVariant(
    select
  ) {
    const block =
      select.closest(
        '[data-ivr-order-bump]'
      );

    if (!block) return;

    const option =
      select.options[
        select.selectedIndex
      ];

    if (!option) return;

    const button =
      block.querySelector(
        '[data-ivr-bump-add]'
      );

    const price =
      block.querySelector(
        '[data-ivr-bump-price]'
      );

    const image =
      block.querySelector(
        '.ivr-cd-bump__media img'
      );

    const installments =
      block.querySelector(
        '.ivr-cd-bump__installments'
      );

    if (button) {
      button.dataset.variantId =
        option.value;

      button.disabled =
        option.disabled;
    }

    if (
      price &&
      option.dataset.price
    ) {
      price.textContent =
        money(
          option.dataset.price
        );
    }

    if (
      image &&
      option.dataset.image
    ) {
      image.src =
        option.dataset.image;
    }

    if (
      installments &&
      option.dataset.price
    ) {
      const count =
        Math.max(
          Number(
            installments.dataset
              .count || 1
          ),
          1
        );

      const template =
        installments.dataset
          .ivrBumpInstallmentsTemplate ||
        'Em até [count]x de [value] sem juros';

      const installmentValue =
        Math.floor(
          Number(
            option.dataset.price
          ) / count
        );

      installments.textContent =
        template
          .replace(
            '[count]',
            String(count)
          )
          .replace(
            '[value]',
            money(
              installmentValue
            )
          );
    }
  }

  async function syncBumpAvailability(select) {
    const block = select.closest('[data-ivr-order-bump]');
    if (!block || block.dataset.hideIfAdded !== 'true') return;

    try {
      const cart = await getCart();
      const inCart = (cart.items || []).some(
        (item) => String(item.variant_id) === String(select.value)
      );
      block.hidden = inCart;
      block.classList.toggle('is-added', inCart);
    } catch (error) {
      console.warn('[IVR Cart Drawer] Não foi possível sincronizar o order bump.', error);
    }
  }

  /* =========================================================
     ORDER BUMP — ADICIONAR
  ========================================================= */

  async function addBump(
    button
  ) {
    const block =
      button.closest(
        '[data-ivr-order-bump]'
      );

    if (!block) return;

    const select =
      block.querySelector(
        '[data-ivr-bump-variant]'
      );

    const variantId =
      Number(
        select?.value ||
        button.dataset.variantId
      );

    if (!variantId) return;

    setLoading(
      button,
      true,
      'Adicionando...'
    );

    try {
      await addItem(
        variantId,

        {
          _ivr_order_bump:
            block.dataset
              .productId ||
            'true',
        },

        button
      );
    } catch (error) {
      buttonError(
        button,
        error
      );

      console.error(
        '[IVR Cart Drawer] Erro no order bump:',
        error
      );
    }
  }

  /* =========================================================
     CUPOM
  ========================================================= */

  async function applyDiscount(
    button
  ) {
    const block =
      button.closest(
        '[data-ivr-discount-block]'
      );

    if (!block) return;

    const input =
      block.querySelector(
        '[data-ivr-discount-input]'
      );

    const info =
      block.querySelector(
        '[data-ivr-discount-info]'
      );

    const code =
      input?.value.trim();

    if (!code) {
      if (info) {
        info.textContent =
          'Digite um código de cupom.';
      }

      input?.focus();

      return;
    }

    setLoading(
      button,
      true,
      'Aplicando...'
    );

    try {
      const redirect =
        encodeURIComponent(
          window.location.pathname +
          window.location.search
        );

      const response =
        await fetch(
          `${root()}discount/${encodeURIComponent(
            code
          )}?redirect=${redirect}`,
          {
            credentials:
              'same-origin',

            redirect:
              'follow',
          }
        );

      if (!response.ok) {
        throw new Error(
          'Não foi possível aplicar o cupom.'
        );
      }

      /*
        Atualiza o carrinho depois
        da Shopify registrar o cupom.
      */

      await refresh(false);

      setLoading(
        button,
        false
      );

      button.textContent =
        'Aplicado';

      button.disabled = true;

      if (info) {
        info.textContent =
          'Cupom enviado. A validação final acontece no checkout.';
      }
    } catch (error) {
      buttonError(
        button,
        error
      );

      if (info) {
        info.textContent =
          'Não foi possível aplicar agora. Tente novamente.';
      }

      console.error(
        '[IVR Cart Drawer] Erro no cupom:',
        error
      );
    }
  }

  /* =========================================================
     TERMOS DO CHECKOUT
  ========================================================= */

  function syncCheckoutState() {
    const element = drawer();
    if (!element) return;

    const required = [
      ...element.querySelectorAll(
        '[data-ivr-terms-block][data-required="true"]'
      ),
    ];

    const accepted = required.every((block) =>
      Boolean(block.querySelector('[data-ivr-terms-checkbox]')?.checked)
    );

    element.querySelectorAll(
      '[data-ivr-checkout], #CartDrawer-Checkout, [data-stackpay-button="1"]'
    ).forEach((button) => {
      button.classList.toggle('is-disabled', !accepted);
      button.setAttribute('aria-disabled', String(!accepted));
    });
  }

  function validateTerms(
    trigger
  ) {
    const element =
      drawer();

    if (!element) {
      return true;
    }

    const blocks =
      [
        ...element.querySelectorAll(
          '[data-ivr-terms-block][data-required="true"]'
        ),
      ];

    let valid = true;

    let firstInvalid =
      null;

    blocks.forEach(
      (block) => {
        const checkbox =
          block.querySelector(
            '[data-ivr-terms-checkbox]'
          );

        const error =
          block.querySelector(
            '[data-ivr-terms-error]'
          );

        const checked =
          Boolean(
            checkbox?.checked
          );

        if (error) {
          error.hidden =
            checked;
        }

        if (!checked) {
          valid = false;

          if (!firstInvalid) {
            firstInvalid =
              checkbox;
          }

          checkbox?.setAttribute(
            'aria-invalid',
            'true'
          );
        } else {
          checkbox?.removeAttribute(
            'aria-invalid'
          );
        }
      }
    );

    if (!valid) {
      trigger?.blur?.();

      firstInvalid?.focus();
    }

    syncCheckoutState();

    return valid;
  }

  function checkoutButton(
    target
  ) {
    if (
      !(target instanceof Element)
    ) {
      return null;
    }

    const button =
      target.closest(
        '[data-ivr-checkout], #CartDrawer-Checkout, [data-stackpay-button="1"]'
      );

    if (!button) {
      return null;
    }

    const currentDrawer =
      drawer();

    if (
      currentDrawer &&
      currentDrawer.contains(button)
    ) {
      return button;
    }

    return null;
  }

  /* =========================================================
     LOCALIZAR BRINDE NO CARRINHO
  ========================================================= */

  function giftLine(
    cart,
    blockId
  ) {
    return (
      cart?.items?.find(
        (item) =>
          String(
            item?.properties
              ?._ivr_gift_block ||
            ''
          ) ===
          String(blockId)
      ) || null
    );
  }

  function qualifyingTotal(cart) {
    return (cart?.items || []).reduce((total, item) => {
      const isGift = String(item?.properties?._ivr_gift || '') === 'true';
      return total + (isGift ? 0 : Number(item?.final_line_price || 0));
    }, 0);
  }

  /* =========================================================
     BRINDE AUTOMÁTICO
  ========================================================= */

  function giftRule(block, total) {
    const threshold = Number(block.dataset.threshold || 0);
    const behavior = block.dataset.giftBehavior || 'highest_only';
    const eligible = total >= threshold;

    return {
      block,
      threshold,
      behavior,
      eligible,
      autoAdd: block.dataset.autoAdd === 'true',
      removeBelow: block.dataset.removeBelow === 'true',
      blockId: block.dataset.blockId,
      variantId: Number(block.dataset.variantId || 0),
    };
  }

  function activeExclusiveGift(rules) {
    return rules
      .filter((rule) => rule.behavior === 'highest_only' && rule.eligible)
      .sort((a, b) => b.threshold - a.threshold)[0] || null;
  }

  function giftShouldBeActive(rule, activeExclusive) {
    if (!rule.eligible) return false;
    if (rule.behavior === 'stackable') return true;
    return Boolean(activeExclusive && activeExclusive.blockId === rule.blockId);
  }

  async function reconcileGifts() {
    const element = drawer();

    if (!element || state.giftLock) {
      return;
    }

    const blocks = [
      ...element.querySelectorAll('[data-ivr-gift]'),
    ];

    if (!blocks.length) {
      return;
    }

    state.giftLock = true;

    try {
      let cart = await getCart();
      cart = await syncMegaFitDiscount(cart);
      const total = qualifyingTotal(cart);
      const rules = blocks.map((block) => giftRule(block, total));
      const activeExclusive = activeExclusiveGift(rules);

      for (const rule of rules) {
        const line = giftLine(cart, rule.blockId);
        const shouldBeActive = giftShouldBeActive(rule, activeExclusive);
        const superseded = rule.behavior === 'highest_only' && !shouldBeActive;
        const shouldRemove = Boolean(
          line && (
            superseded ||
            (rule.behavior === 'highest_only' && !rule.eligible) ||
            (!rule.eligible && rule.removeBelow)
          )
        );

        if (!shouldRemove) continue;

        await changeItem(
          line.key,
          0,
          rule.block
        );

        return;
      }

      for (const rule of rules) {
        const shouldBeActive = giftShouldBeActive(rule, activeExclusive);
        if (!shouldBeActive || !rule.autoAdd || !rule.variantId) continue;

        const line = giftLine(cart, rule.blockId);
        if (line) continue;

        await addItem(
          rule.variantId,
          {
            _ivr_gift_block: rule.blockId,
            _ivr_gift: 'true',
          },
          rule.block
        );

        return;
      }
    } catch (error) {
      console.error(
        '[IVR Cart Drawer] Erro no brinde automático:',
        error
      );
    } finally {
      state.giftLock = false;
    }
  }

  /* =========================================================
     BRINDE MANUAL
  ========================================================= */

  async function addGift(
    button
  ) {
    const block =
      button.closest(
        '[data-ivr-gift]'
      );

    if (!block) return;

    const variantId =
      Number(
        block.dataset
          .variantId || 0
      );

    if (!variantId) return;

    try {
      const cart = await getCart();
      const total = qualifyingTotal(cart);
      const allBlocks = [...(drawer()?.querySelectorAll('[data-ivr-gift]') || [])];
      const rules = allBlocks.map((item) => giftRule(item, total));
      const activeExclusive = activeExclusiveGift(rules);
      const currentRule = rules.find((rule) => rule.blockId === block.dataset.blockId);

      if (!currentRule || !giftShouldBeActive(currentRule, activeExclusive)) {
        scheduleGifts();
        return;
      }
    } catch (error) {
      console.error('[IVR Cart Drawer] Não foi possível validar a faixa do brinde.', error);
      return;
    }

    setLoading(
      button,
      true,
      'Adicionando...'
    );

    try {
      await addItem(
        variantId,

        {
          _ivr_gift_block:
            block.dataset.blockId,

          _ivr_gift:
            'true',
        },

        button
      );
    } catch (error) {
      buttonError(
        button,
        error
      );

      console.error(
        '[IVR Cart Drawer] Erro ao adicionar brinde:',
        error
      );
    }
  }

  /* =========================================================
     AGENDAR VERIFICAÇÃO DO BRINDE
  ========================================================= */

  function scheduleGifts() {
    clearTimeout(
      state.timer
    );

    state.timer =
      setTimeout(
        reconcileGifts,
        250
      );
  }

  /* =========================================================
     OBSERVAR ALTERAÇÕES NO DRAWER
  ========================================================= */

  function observeDrawer() {
    const element =
      drawer();

    if (
      !element ||
      state.observedDrawer ===
        element
    ) {
      return;
    }

    if (state.observer) {
      state.observer
        .disconnect();
    }

    state.observedDrawer =
      element;

    state.observer =
      new MutationObserver(
        (mutations) => {
          const relevant =
            mutations.some(
              (mutation) =>
                mutation.type ===
                  'childList' &&
                mutation.addedNodes
                  .length > 0
            );

          if (relevant) {
            scheduleGifts();
            syncCheckoutState();
          }
        }
      );

    state.observer.observe(
      element,
      {
        childList: true,
        subtree: true,
      }
    );
  }

  /* =========================================================
     INICIALIZAR
  ========================================================= */

  function init() {
    observeDrawer();
    syncCheckoutState();
    scheduleGifts();
  }

  /* =========================================================
     SUBSCRIBE NATIVO DO DAWN
  ========================================================= */

  function bindPubSub() {
    if (
      state.subscribed
    ) {
      return true;
    }

    if (
      typeof window.subscribe !==
        'function' ||
      !window.PUB_SUB_EVENTS
        ?.cartUpdate
    ) {
      return false;
    }

    try {
      window.subscribe(
        window.PUB_SUB_EVENTS
          .cartUpdate,

        () => {
          scheduleGifts();
          syncMegaFitFromCart();
        }
      );

      state.subscribed =
        true;

      return true;
    } catch (error) {
      return false;
    }
  }

  function boot() {
    init();

    /*
      Caso pubsub.js/global.js ainda
      não tenha terminado de carregar.
    */

    if (!bindPubSub()) {
      let attempts = 0;

      const interval =
        setInterval(
          () => {
            attempts += 1;

            if (
              bindPubSub() ||
              attempts >= 20
            ) {
              clearInterval(
                interval
              );
            }
          },

          250
        );
    }
  }

  /* =========================================================
     EVENTO: TROCAR VARIAÇÃO
  ========================================================= */

  document.addEventListener(
    'change',
    (event) => {
      if (
        !(
          event.target
          instanceof Element
        )
      ) {
        return;
      }

      const select =
        event.target.closest(
          '[data-ivr-bump-variant]'
        );

      if (select) {
        changeBumpVariant(
          select
        );

        syncBumpAvailability(select);

        return;
      }

      /*
        Limpa erro dos termos
        quando marcar checkbox.
      */

      if (
        event.target.matches(
          '[data-ivr-terms-checkbox]'
        ) &&
        event.target.checked
      ) {
        const block =
          event.target.closest(
            '[data-ivr-terms-block]'
          );

        const error =
          block?.querySelector(
            '[data-ivr-terms-error]'
          );

        if (error) {
          error.hidden =
            true;
        }

        event.target
          .removeAttribute(
            'aria-invalid'
          );

        syncCheckoutState();
      } else if (event.target.matches('[data-ivr-terms-checkbox]')) {
        syncCheckoutState();
      }
    }
  );

  /* =========================================================
     EVENTO: CLIQUES INTERNOS
  ========================================================= */

  document.addEventListener(
    'click',
    (event) => {
      if (
        !(
          event.target
          instanceof Element
        )
      ) {
        return;
      }

      /*
        ORDER BUMP
      */

      const bump =
        event.target.closest(
          '[data-ivr-bump-add]'
        );

      if (bump) {
        event.preventDefault();

        addBump(bump);

        return;
      }

      /*
        CUPOM
      */

      const discount =
        event.target.closest(
          '[data-ivr-discount-apply]'
        );

      if (discount) {
        event.preventDefault();

        applyDiscount(
          discount
        );

        return;
      }

      /*
        BRINDE
      */

      const gift =
        event.target.closest(
          '[data-ivr-gift-add]'
        );

      if (gift) {
        event.preventDefault();

        addGift(gift);

        return;
      }
    }
  );

  /* =========================================================
     EVENTO: VALIDAR TERMOS ANTES DO CHECKOUT
  ========================================================= */

  document.addEventListener(
    'click',

    (event) => {
      const button =
        checkoutButton(
          event.target
        );

      if (!button) {
        return;
      }

      if (
        !validateTerms(
          button
        )
      ) {
        event.preventDefault();

        event.stopImmediatePropagation();
      }
    },

    true
  );

  /* =========================================================
     EVENTO: ENTER NO CUPOM
  ========================================================= */

  document.addEventListener(
    'keydown',
    (event) => {
      if (
        event.key !== 'Enter'
      ) {
        return;
      }

      if (
        !(
          event.target
          instanceof Element
        )
      ) {
        return;
      }

      const input =
        event.target.closest(
          '[data-ivr-discount-input]'
        );

      if (!input) {
        return;
      }

      event.preventDefault();

      const block =
        input.closest(
          '[data-ivr-discount-block]'
        );

      const button =
        block?.querySelector(
          '[data-ivr-discount-apply]'
        );

      if (button) {
        applyDiscount(
          button
        );
      }
    }
  );

  /* =========================================================
     EDITOR DE TEMA SHOPIFY
  ========================================================= */

  document.addEventListener(
    'shopify:section:load',
    () => {
      init();
    }
  );

  document.addEventListener(
    'shopify:block:select',
    () => {
      init();

      if (
        window.Shopify
          ?.designMode
      ) {
        openDrawer();
      }
    }
  );

  document.addEventListener(
    'shopify:section:select',
    (event) => {
      if (
        !window.Shopify
          ?.designMode
      ) {
        return;
      }

      if (
        event.target.closest?.(
          '.cart-drawer-section'
        )
      ) {
        openDrawer();
      }
    }
  );

  /* =========================================================
     EVENTO PERSONALIZADO
  ========================================================= */

  document.addEventListener(
    'cart:updated',
    () => {
      scheduleGifts();
      syncMegaFitFromCart();
    }
  );

  /* =========================================================
     BOOT
  ========================================================= */

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once: true,
      }
    );
  } else {
    boot();
  }

  /* =========================================================
     API GLOBAL
  ========================================================= */

  window.IVRCartDrawer = {
    version: VERSION,

    refresh,

    reconcileGifts,

    open: openDrawer,
  };
})();